/**
 * Items Vuex module for Portal Application
 * Controls the data and actions for Items
 * 
 * @module Items
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date August 2023
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
    items: [],
    classCodes: [],
    maiShortCodes: [],
    itemGroups: [],
    unipharmItemGroups: [],
    vendors: [],
    myTopItems: [],
    itemLookupData: [],
    itemDetails: [],
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getItems(st) {
        return st.items;
    },

    getClassCodes(st) {
        return st.classCodes;
    },

    getMaiShortCodes(st) {
        return st.maiShortCodes;
    },

    getItemGroups(st) {
        return st.itemGroups;
    },

    getUnipharmItemGroups(st) {
        return st.unipharmItemGroups;
    },

    getVendors(st) {
        return st.vendors;
    },

    getMyTopItems(st) {
        return st.myTopItems;
    },

    getItemLookupData(st) {
        return st.itemLookupData;
    },

    getItemDetails(st) {
        return st.itemDetails;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },

    setItems(st, payload) {
        st.items = payload;
    },

    setClassCodes(st, payload) {
        st.classCodes = payload;
    },

    setMaiShortCodes(st, payload) {
        st.maiShortCodes = payload;
    },

    setItemGroups(st, payload) {
        st.itemGroups = payload;
    },

    setUnipharmItemGroups(st, payload) {
        st.unipharmItemGroups = payload;
    },

    setVendors(st, payload) {
        st.vendors = payload;
    },

    setMyTopItems(st, payload) {
        st.myTopItems = payload;
    },

    setItemLookupData(st, payload) {
        st.itemLookupData = payload;
    },

    setItemDetails(st, payload) {
        st.itemDetails = payload;
    },
};

const actions = {

    // Get  Items
    async fetchItems(context) 
    {
        var commit = context.commit;

        if (context.state.items.length === 0) {
            const response = await portalapi.moduleItem.getAllActiveItems();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setItems", response.data);
            }
        }
    },


    // Get Class items
    async getClassItems({ commit }, classCode) 
    {
        const currentItems = state.items;

        if (classCode === 'all') {
            return currentItems;
        }

        let classItems = [];

        for (let i = 0; i < currentItems.length; i++) {
            if (currentItems[i].class === classCode) {
                classItems.push(currentItems[i]);
            }
        }

        return classItems;
    },


    // Get Class Codes
    async fetchClassCodes(context) 
    {
        var commit = context.commit;

        if (context.state.classCodes.length === 0) {
            const response = await portalapi.moduleItem.getClassCodes();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setClassCodes", response.data);
            }
        }
    },


    // Get Mai Short Codes
    async fetchMaiShortCodes({ commit }) 
    {
        const response = await portalapi.moduleItem.getMaiShortCodes();
        
        commit("setMaiShortCodes", response.maiShortCodes);
    },


    // Get Item Groups
    async fetchItemGroups(context) 
    {
        var commit = context.commit;

        if (context.state.itemGroups.length === 0) {
            const response = await portalapi.moduleItem.getItemGroups();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setItemGroups", response.data);
            }
        }
    },


    // Get Unipharm Item Groups
    async fetchUnipharmItemGroups(context) 
    {
        var commit = context.commit;

        if (context.state.unipharmItemGroups.length === 0) {
            const response = await portalapi.moduleItem.getUnipharmItemGroups();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setUnipharmItemGroups", response.data);
            }
        }
    },


    // Get Vendors
    async fetchVendors(context) 
    {
        var commit = context.commit;

        if (context.state.vendors.length === 0) {
            const response = await portalapi.moduleItem.getVendorCodes();

            if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setVendors", response.data);
            }
        }
    },


    // TODO: To be implemented post go live
    /*
    // Get My Top Items
    async fetchMyTopItems({ commit }, payload) 
    {
        const response = await portalapi.moduleItem.getUsersTopItemsData(payload);

        // TODO - Remove for loop when implementing APIs
        for (
            let i = 0;
            i < response.length;
            i++
        ) {
            response[i].quantityOrdered = Math.floor(Math.random() * 10);
        }

        // TODO - Implement api call validation

        commit("setMyTopItems", response);
    },
    */

    
    // Fetch Item Lookup Data
    async fetchItemLookupData(context) 
    {
        var commit = context.commit;

        if (context.state.itemLookupData.length === 0) {
            const response = await portalapi.moduleItem.getItemLookupData();
            
            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setItemLookupData", response.data);
            }
        }
    },


    // Get Item Details
    async fetchItemDetails({ commit }, itemNumber) 
    {
        commit("setItemDetails", null);

        const response = await portalapi.moduleItem.getItemDetails(itemNumber);

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setItemDetails", response.data);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
